import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import API_URL from "../config/api";
import { fetchTeamOptions } from "../utils/fetchTeams";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ' -]{2,}$/;
const DISPOSABLE_EMAIL_DOMAINS = [
  "10minutemail.com",
  "guerrillamail.com",
  "mailinator.com",
  "tempmail.com",
  "yopmail.com",
];

const isDisposableEmail = (value) => {
  const domain = value.trim().toLowerCase().split("@")[1];
  return DISPOSABLE_EMAIL_DOMAINS.includes(domain);
};

const getClubOptionValue = (club) =>
  club.id ? String(club.id) : club.team_name || club.name || "";

function Register({ accountType }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [position, setPosition] = useState("");
  const [city, setCity] = useState("");
  const [preferredFoot, setPreferredFoot] = useState("");
  const [playerTeamId, setPlayerTeamId] = useState("");
  const [playerTeamName, setPlayerTeamName] = useState("");
  const [noTeam, setNoTeam] = useState(false);
  const [bio, setBio] = useState("");
  const [teamName, setTeamName] = useState("");
  const [level, setLevel] = useState("");
  const [category, setCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [clubsError, setClubsError] = useState("");
  const [formMessage, setFormMessage] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const navigate = useNavigate();
  const { dashboardPath, isAuthenticated } = useAuth();

  const isTeam = accountType === "team";

  useEffect(() => {
    if (isAuthenticated) {
      navigate(dashboardPath, { replace: true });
    }
  }, [dashboardPath, isAuthenticated, navigate]);

  useEffect(() => {
    if (isTeam) return;

    const loadClubs = async () => {
      try {
        setLoadingClubs(true);
        setClubsError("");
        const teamOptions = await fetchTeamOptions();
        setClubs(teamOptions);
      } catch (err) {
        console.log(err);
        setClubs([]);
        setClubsError(
          "Impossible de charger les clubs. Tu peux cocher sans equipe et faire la demande plus tard."
        );
      } finally {
        setLoadingClubs(false);
      }
    };

    loadClubs();
  }, [isTeam]);

  const handleClubSelect = (clubValue) => {
    const club = clubs.find((c) => getClubOptionValue(c) === clubValue);
    setPlayerTeamId(clubValue);
    setPlayerTeamName(club ? club.team_name : "");
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setFormMessage(null);

    const cleanName = name.trim();
    const cleanEmail = email.trim().toLowerCase();
    const cleanCity = city.trim();
    const cleanTeamName = teamName.trim();
    const cleanBio = bio.trim();
    const cleanLevel = level.trim();
    const cleanCategory = category.trim();

    if (!NAME_REGEX.test(cleanName)) {
      setFormMessage({
        type: "error",
        text: "Le nom doit contenir au moins 2 lettres et aucun chiffre.",
      });
      return;
    }

    if (!EMAIL_REGEX.test(cleanEmail) || isDisposableEmail(cleanEmail)) {
      setFormMessage({
        type: "error",
        text: "Entre une adresse email valide et non temporaire.",
      });
      return;
    }

    if (password.length < 8 || !/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setFormMessage({
        type: "error",
        text: "Le mot de passe doit contenir au moins 8 caractères, avec lettres et chiffres.",
      });
      return;
    }

    if (password !== confirmPassword) {
      setFormMessage({
        type: "error",
        text: "Les mots de passe ne correspondent pas.",
      });
      return;
    }

    if (!cleanCity || /\d/.test(cleanCity)) {
      setFormMessage({
        type: "error",
        text: "La ville est obligatoire et ne doit pas contenir de chiffre.",
      });
      return;
    }

    if (!privacyAccepted) {
      setFormMessage({
        type: "error",
        text: "Tu dois accepter la politique de confidentialite pour creer un compte.",
      });
      return;
    }

    if (!isTeam) {
      if (!position || !preferredFoot) {
        setFormMessage({
          type: "error",
          text: "Sélectionne ton poste et ton pied fort.",
        });
        return;
      }

    }

    if (isTeam && (!cleanTeamName || !cleanLevel || !cleanCategory)) {
      setFormMessage({
        type: "error",
        text: "Nom d'équipe, niveau et catégorie sont obligatoires.",
      });
      return;
    }

    const endpoint = isTeam
      ? `${API_URL}/api/auth/register/team`
      : `${API_URL}/api/auth/register/player`;

    const payload = isTeam
      ? {
          name: cleanName,
          email: cleanEmail,
          password,
          team_name: cleanTeamName,
          city: cleanCity,
          level: cleanLevel,
          category: cleanCategory,
          bio: cleanBio,
          privacy_consent: privacyAccepted,
          privacy_policy_version: "2026-05-22",
        }
      : {
          name: cleanName,
          email: cleanEmail,
          password,
          position,
          city: cleanCity,
          preferred_foot: preferredFoot,
          team_id:
            !noTeam && /^\d+$/.test(String(playerTeamId))
              ? Number(playerTeamId)
              : null,
          team_name: playerTeamName,
          no_team: noTeam,
          bio: cleanBio,
          privacy_consent: privacyAccepted,
          privacy_policy_version: "2026-05-22",
        };

    try {
      setLoading(true);

      const res = await axios.post(endpoint, payload);

      navigate("/login", {
        replace: true,
        state: {
          message: res.data.message || "Compte créé. Tu peux maintenant te connecter.",
        },
      });
    } catch (err) {
      console.log(err);
      const message = err.response?.data?.error
        ? `${err.response.data.message}: ${err.response.data.error}`
        : err.response?.data?.message || "Erreur lors de l'inscription";

      setFormMessage({ type: "error", text: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <form className="login-form register-form" onSubmit={handleRegister}>
        <button className="back-btn" type="button" onClick={() => navigate("/")}>
          Retour
        </button>

        <h2>{isTeam ? "Inscription Équipe" : "Inscription Joueur"}</h2>
        <div className="auth-onboarding">
          <span>{isTeam ? "Club" : "Joueur"}</span>
          <strong>
            {isTeam
              ? "Crée ta vitrine club en quelques minutes."
              : "Crée ton profil et demande à rejoindre un club."}
          </strong>
          <p>
            Après l'inscription, vérifie ton email puis connecte-toi pour
            compléter ton espace.
          </p>
        </div>

        {formMessage && (
          <p className={`auth-notice auth-notice-${formMessage.type}`}>
            {formMessage.text}
          </p>
        )}

        <label htmlFor="reg-name" className="sr-only">{isTeam ? "Nom du responsable" : "Nom complet"}</label>
        <input
          id="reg-name"
          type="text"
          placeholder={isTeam ? "Nom du responsable" : "Nom complet"}
          value={name}
          onChange={(e) => setName(e.target.value)}
          minLength="2"
          pattern="[A-Za-zÀ-ÖØ-öø-ÿ' -]+"
          title="Le nom ne doit pas contenir de chiffre."
          required
        />

        <label htmlFor="reg-email" className="sr-only">Email</label>
        <input
          id="reg-email"
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />

        <label htmlFor="reg-password" className="sr-only">Mot de passe</label>
        <input
          id="reg-password"
          type="password"
          placeholder="Mot de passe"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          minLength="8"
          title="Au moins 8 caractères, avec lettres et chiffres."
          required
        />

        <label htmlFor="reg-confirm" className="sr-only">Confirmer le mot de passe</label>
        <input
          id="reg-confirm"
          type="password"
          placeholder="Confirmer le mot de passe"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          minLength="8"
          required
        />

        {isTeam ? (
          <>
            <input
              type="text"
              placeholder="Nom de l'équipe"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              required
            />

            <input
              type="text"
              placeholder="Ville"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              pattern="[A-Za-zÀ-ÖØ-öø-ÿ' -]+"
              title="La ville ne doit pas contenir de chiffre."
              required
            />

            <input
              type="text"
              placeholder="Niveau"
              value={level}
              onChange={(e) => setLevel(e.target.value)}
              required
            />

            <input
              type="text"
              placeholder="Catégorie"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            />
          </>
        ) : (
          <>
            <select
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              required
            >
              <option value="">Poste</option>
              <option value="Gardien">Gardien</option>
              <option value="Defenseur">Défenseur</option>
              <option value="Milieu">Milieu</option>
              <option value="Attaquant">Attaquant</option>
            </select>

            <input
              type="text"
              placeholder="Ville"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              pattern="[A-Za-zÀ-ÖØ-öø-ÿ' -]+"
              title="La ville ne doit pas contenir de chiffre."
              required
            />

            <select
              value={preferredFoot}
              onChange={(e) => setPreferredFoot(e.target.value)}
              required
            >
              <option value="">Pied fort</option>
              <option value="Droit">Droit</option>
              <option value="Gauche">Gauche</option>
              <option value="Deux pieds">Deux pieds</option>
            </select>

            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={noTeam}
                onChange={(e) => setNoTeam(e.target.checked)}
              />
              Je n'ai pas d'équipe
            </label>

            {!noTeam && (
              <>
                {loadingClubs ? (
                  <p className="info-text">Chargement des clubs...</p>
                ) : clubs.length > 0 ? (
                  <select
                    value={playerTeamId}
                    onChange={(e) => handleClubSelect(e.target.value)}
                    required
                  >
                    <option value="">Club à rejoindre sur demande</option>
                    {clubs.map((club) => (
                      <option
                        key={getClubOptionValue(club)}
                        value={getClubOptionValue(club)}
                      >
                        {club.team_name} ({club.city})
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="info-text">Aucun club enregistré pour le moment.</p>
                )}
                {clubsError && <p className="info-text">{clubsError}</p>}
                <p className="info-text">
                  Le club devra accepter ta demande avant que tu rejoignes son effectif.
                </p>
              </>
            )}
          </>
        )}

        <textarea
          placeholder={isTeam ? "Description de l'équipe" : "Bio courte"}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows="4"
        />

        <label className="checkbox-row privacy-consent-row">
          <input
            type="checkbox"
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            required
          />
          <span>
            J'accepte la{" "}
            <Link to="/confidentialite" target="_blank" rel="noreferrer">
              politique de confidentialite
            </Link>
            .
          </span>
        </label>

        <button type="submit" disabled={loading}>
          {loading ? "Création..." : "Créer mon compte"}
        </button>
      </form>
    </div>
  );
}

export default Register;

